import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { useTexts } from "../../contexts/TextsContext";
import { TEXT_SECTIONS } from "../../lib/textDefaults";
import { ArrowLeft, Save, RotateCcw, Search } from "lucide-react";

export default function SiteTextsAdmin() {
  const navigate = useNavigate();
  const { reload } = useTexts();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(TEXT_SECTIONS[0].id);

  useEffect(() => {
    api.get("/site-content/ui_texts").then(r => {
      setValues((r.data && r.data.value && typeof r.data.value === "object") ? r.data.value : {});
    }).catch(() => setValues({}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setVal = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const resetVal = (k) => setValues(prev => { const n = { ...prev }; delete n[k]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content/ui_texts", { value: values });
      await reload();
      toast.success("Textes enregistrés. Le site est mis à jour.");
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return TEXT_SECTIONS;
    const ql = q.toLowerCase();
    return TEXT_SECTIONS.map(s => ({
      ...s,
      fields: s.fields.filter(f =>
        f.key.toLowerCase().includes(ql) ||
        f.label.toLowerCase().includes(ql) ||
        (f.default || "").toLowerCase().includes(ql)
      ),
    })).filter(s => s.fields.length > 0);
  }, [q]);

  const overridenCount = Object.keys(values).filter(k => values[k] !== undefined && values[k] !== "").length;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>

        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Textes du site</h1>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
              Personnalisez chaque libellé, bouton et message du site public. {overridenCount > 0 && <span className="font-bold">{overridenCount} texte(s) personnalisé(s).</span>}
            </p>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-texts"
            className="btn-brutal btn-brutal-solar px-6 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50">
            {saving ? "Enregistrement..." : "Enregistrer"} <Save className="w-4 h-4" />
          </button>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          <aside className="space-y-2 lg:sticky lg:top-24 self-start">
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un texte..." className="input pl-9" data-testid="texts-search" />
            </div>
            {filtered.map(s => (
              <a key={s.id} href={`#sec-${s.id}`} onClick={() => setActive(s.id)}
                className={`block px-4 py-3 border text-sm font-bold uppercase tracking-wider transition-colors ${active === s.id ? "bg-[color:var(--solar)] border-slate-900" : "bg-[color:var(--bg-surface)] hover:border-slate-900"}`}
                style={{ borderColor: "var(--border-default)" }} data-testid={`section-link-${s.id}`}>
                {s.title}
              </a>
            ))}
          </aside>

          <div className="space-y-10">
            {filtered.map(s => (
              <div key={s.id} id={`sec-${s.id}`} className="bg-[color:var(--bg-surface)] border p-8" style={{ borderColor: "var(--border-default)" }}>
                <h2 className="font-display font-black text-xl tracking-tight">{s.title}</h2>
                {s.desc && <p className="text-xs mt-1 mb-6" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>}
                <div className="space-y-5 mt-4">
                  {s.fields.map(f => {
                    const customised = values[f.key] !== undefined && values[f.key] !== "";
                    const val = customised ? values[f.key] : "";
                    return (
                      <div key={f.key} data-testid={`field-${f.key}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>
                            {f.label} {customised && <span className="ml-2 inline-block text-[10px] bg-[color:var(--solar)] text-slate-900 px-2 py-0.5">Modifié</span>}
                          </label>
                          {customised && (
                            <button type="button" onClick={() => resetVal(f.key)} title="Réinitialiser au texte par défaut"
                              className="text-[10px] uppercase tracking-wider flex items-center gap-1 opacity-60 hover:opacity-100">
                              <RotateCcw className="w-3 h-3" /> Réinitialiser
                            </button>
                          )}
                        </div>
                        <div className="text-[11px] mb-2 font-mono opacity-50">Défaut : « {f.default} »</div>
                        {f.type === "textarea" ? (
                          <textarea rows={3} value={val} onChange={e => setVal(f.key, e.target.value)}
                            placeholder={f.default} className="input resize-none w-full" />
                        ) : (
                          <input value={val} onChange={e => setVal(f.key, e.target.value)}
                            placeholder={f.default} className="input w-full" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-16 text-sm" style={{ color: "var(--text-secondary)" }}>Aucun texte ne correspond.</div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
